<template>
  <v-card-text class="pa-0 mt-3 mb-3" data-testid="cart-item-list">
    <v-row
      class="align-center product-layout"
      v-for="product in cart"
      :key="product.id"
    >
      <v-col cols="2" md="2">
        <!--
          The garment thumbnail. Square by construction -- one value
          drives both axes so a future edit cannot distort the photo by
          changing only one of them. See ``thumbnailSize`` for why the
          value is not a constant.
        -->
        <v-img
          :height="thumbnailSize"
          :width="thumbnailSize"
          :cover="false"
          :src="getImage(product.image)"
          :data-testid="`cart-product-thumbnail-${product.index}`"
        />
        <!--
          Artifi PR 6b: cached preview thumbnail for customizable
          lines. Rendered only when the cart line has
          personalization_values (i.e. the shopper entered per-garment
          customization at add-to-cart time). For non-customizable
          lines this whole block is omitted to keep the legacy row
          layout pixel-stable.

          SIZED FROM ``thumbnailSize``, the same computed that drives the
          garment image above -- this preview sits in the SAME
          ``<v-col cols="2">``, so it is under the same phone constraint
          documented on THUMBNAIL_SIZE_MOBILE, and a second constant here
          would be a second source of truth for one question.

          All three branches share ``previewBoxStyle`` so the box is the
          same size whether or not a preview has resolved. See that
          computed for why that matters.
        -->
        <div
          v-if="isCustomizableLine(product)"
          class="cart-preview-thumb mt-2"
          :data-testid="`cart-preview-thumbnail-${product.index}`"
        >
          <img
            v-if="previewState(product).preview_url"
            :src="previewState(product).preview_url"
            alt="Personalization preview"
            :style="previewBoxStyle"
          />
          <!-- A preview is on its way ('loading' = our fetch in flight,
               'rendering' = the server is still producing it): the shared
               shimmer, same as everywhere the UI waits on a render. -->
          <!-- Decorative: the row's text carries the state for assistive tech
               (no live region per row). -->
          <template v-else-if="isPreviewPending(product)">
            <RenderShimmer
              :data-testid="`cart-preview-shimmer-${product.index}`"
              :style="previewShimmerStyle"
              aria-hidden="true"
            />
            <span
              style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap"
            >Preview generating</span>
          </template>
          <span
            v-else
            class="preview-placeholder"
            :style="previewPlaceholderStyle"
          >Preview unavailable</span>
        </div>
      </v-col>
      <v-col cols="3" md="3">
        {{ product.product_name }}<br />
        <span
          >{{ product.primary_attribute_value }}, &nbsp;{{
            product.secondary_attribute_value
          }}</span
        >
        <!--
          The logo the shopper picked, named.

          The PDP blocks add-to-cart until a logo is chosen when a product
          offers more than one, so by the time a line reaches the cart the
          choice is made and validated -- but nothing showed it back. Two
          AdventHealth tees differing only by logo are two distinct cart
          lines by design (``_artwork_key`` makes the logo part of line
          identity), and without this caption they render identically.

          The name is READ from ``logo_names``, the map the server
          resolves through the same label rule the PDP picker uses. The
          cart deliberately does NOT parse the filename itself: it would
          be free to drift from the name the shopper chose one screen
          earlier.

          Rendered only when there IS a name, so an undecorated line --
          the common case -- shows no caption rather than a bare "Logo:"
          with nothing after it.
        -->
        <div
          v-if="logoName(product)"
          class="cart-line-logo mt-1"
          :data-testid="`cart-line-logo-${product.index}`"
          style="font-size: 11px; color: #555;"
        >
          Logo: {{ logoName(product) }}
        </div>
        <!--
          Artifi PR 6b: per-garment personalization values display.
          Each value pairs with the field label from
          personalization_fields[0] (PR 6a's flat-list shape) or
          falls back to "Personalization N" when fields are absent.
          Multi-value rendering inlines for short lists; long lists
          could later collapse to a "View all" expand. Today the
          shopper sees the full list -- typical orders are small.
        -->
        <div
          v-if="isCustomizableLine(product)"
          class="cart-personalization-list mt-1"
          :data-testid="`cart-personalization-list-${product.index}`"
          style="font-size: 11px; color: #555;"
        >
          <div
            v-for="(value, vIdx) in personalizationValues(product)"
            :key="vIdx"
            class="cart-personalization-row"
          >
            <span class="cart-personalization-label"
              >{{ personalizationLabel(product, vIdx) }}:</span
            >
            {{ value }}
          </div>
        </div>
      </v-col>
      <v-col cols="4" md="4">
        <div class="d-flex justify-center align-center">
          <!-- Per-line handles. The buttons keep the shared co-add /
               co-subtract / co-remove testids (many scenarios click them),
               and an element carries ONE data-testid, so the index-keyed
               handle sits on a wrapper. A click on the wrapper's centre
               lands on the button inside it. This is what lets a scenario
               press + on the SECOND of two same-size logo lines. -->
          <span
            class="cart-line-control"
            style="display: inline-flex"
            :data-testid="`cart-line-subtract-${product.index}`"
          >
            <v-btn
              :disabled="
                quantityDecreaseDisabled(product) ||
                  calculatingShipping ||
                  salesTaxLoading
              "
              @click="decreaseQuantity(product)"
              icon variant="plain"
              data-testid="co-subtract"
              >-</v-btn
            >
          </span>
          <!-- Editable line quantity (locked decision 21): correcting a
               count of 24 to 12 used to mean twelve taps on co-subtract.

               Commits on `change` (blur / Enter), NOT on every keystroke:
               each write is an HTTP round trip that recalculates shipping
               and tax, so per-digit commits would fire a request for the
               intermediate "1" on the way to typing "12".

               FLOORS AT 1, not 0 -- unlike the PDP, where 0 means "not
               ordered". Deleting a line is co-remove's job; a 0 here
               would silently remove the row being edited. -->
          <input
            type="number"
            min="1"
            max="9999"
            step="1"
            inputmode="numeric"
            class="cart-line-qty"
            style="
              width: 56px;
              min-height: 44px;
              text-align: center;
              font-size: 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 4px;
              box-sizing: border-box;
            "
            :value="product.quantity"
            :disabled="calculatingShipping || salesTaxLoading"
            :aria-label="`Quantity for ${product.product_name}`"
            :data-testid="`cart-line-qty-${product.index}`"
            @change="onQuantityCommit(product, $event)"
          />
          <span
            class="cart-line-control"
            style="display: inline-flex"
            :data-testid="`cart-line-add-${product.index}`"
          >
            <v-btn
              :disabled="
                quantityIncreaseDisabled(product) ||
                  calculatingShipping ||
                  salesTaxLoading
              "
              @click="increaseQuantity(product)"
              icon variant="plain"
              data-testid="co-add"
              >+</v-btn
            >
          </span>
        </div>
      </v-col>
      <v-col v-if="campaign.storefront_pricing" cols="2" md="2">
        <!-- Per-line price handle. Keyed by the cart line's stable `index`,
             the same key the thumbnail and personalization-list handles use
             above, so the Nth cart row's price is addressable without a
             product id. NOTE the enclosing v-if: this column renders only
             when the store has storefront_pricing ON, so a scenario
             asserting on it must run against such a store -- otherwise the
             assertion is vacuously satisfied by an absent element. -->
        <span
          v-if="product.price != null"
          :data-testid="`cart-line-price-${product.index}`"
        >
          ${{
            salesPriceFormat(applyFundraisingAmount(product.price))
          }}
        </span>
        <!-- No server price for this line (cart_prices failed or omitted
             it). A placeholder, never "$0.00": the shared formatter turns
             null into 0.00, which reads as a real, free price. -->
        <span
          v-else
          title="Price unavailable"
          :data-testid="`cart-line-price-unavailable-${product.index}`"
          >&mdash;</span
        >
      </v-col>
      <v-col cols="1" md="1">
        <span
          class="cart-line-control"
          style="display: inline-flex"
          :data-testid="`cart-line-remove-${product.index}`"
        >
          <v-btn
            :disabled="calculatingShipping || salesTaxLoading"
            variant="plain"
            icon
            size="small"
            @click="deleteItem(product)"
            data-testid="co-remove"
            ><v-icon icon="delete" /></v-btn
          >
        </span>
      </v-col>
    </v-row>

    <!-- Empty Cart: sits directly below the cart rows, just above the
         order summary. Guarded by a confirm dialog so a stray click
         cannot silently wipe the shopper's cart. Hidden when the cart
         is already empty. -->
    <v-row v-if="cart && cart.length" class="justify-end mt-2" no-gutters>
      <v-btn
        variant="text"
        size="small"
        color="error"
        :disabled="calculatingShipping || salesTaxLoading"
        @click="showEmptyCartDialog = true"
        data-testid="co-empty-cart"
      >Remove all items</v-btn>
    </v-row>

    <v-dialog v-model="showEmptyCartDialog" max-width="480">
      <v-card>
        <v-card-title class="text-h5">Empty your cart?</v-card-title>
        <v-divider></v-divider>
        <v-card-text data-testid="co-empty-cart-body">
          This removes all items from your cart. This cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showEmptyCartDialog = false"
            data-testid="co-empty-cart-cancel"
          >Cancel</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :disabled="emptyingCart"
            @click="confirmEmptyCart"
            data-testid="co-empty-cart-confirm"
          >Remove all items</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card-text>
</template>

<script>
import helpers from "@/mixins/helpers";
import { artworkKey } from "./cartLinePrices";
import { STOREFRONT_STORE_ID, cartStoreArgs } from "@/router/storefrontStoreId";
import { hidesLoneLogo } from "@/utils/loneLogo";
import RenderShimmer from "@/components/decoration/RenderShimmer.vue";

// Matches the mobile PDP (ProductPage.vue). One breakpoint across the
// shopper surfaces rather than a second, divergent definition of "phone".
const MOBILE_MEDIA_QUERY = "(max-width: 860px)";

// Garment thumbnail, desktop. Jake's value (2026-09-17): the rows around
// it now carry four lines of copy -- product name, colour, size, and the
// chosen logo's name (#3600) -- and the previous 45px square read as an
// afterthought beside them.
const THUMBNAIL_SIZE_DESKTOP = "90px";

// Garment thumbnail, phone. NOT a design preference -- a hard layout
// constraint. The image sits in ``<v-col cols="2">``, and ``cols`` is the
// xs-and-up BASE, so that column is 16.67% of the row at EVERY viewport.
// On a 375px phone its content box measures about 30px once the
// container's 16px padding, the cart column's gutter, the card's ``pa-2``
// and the column's own 12px gutters are taken out. Vuetify renders
// ``width``/``height`` as INLINE styles with no shrink
// (composables/dimensions.js), so a fixed 90px here would not be clipped
// to the column -- it would overhang the product name beside it.
//
// 45px keeps the phone layout exactly as it shipped. The squeeze is
// pre-existing and is NOT made worse here; widening the column on mobile
// is the real fix and is a layout change beyond this one.
const THUMBNAIL_SIZE_MOBILE = "45px";

// A line the server reports as still ``rendering`` is re-fetched with
// doubling delays (2s, 4s, 8s, 16s, 32s); if it is still not ready after
// that, it settles to ``unavailable`` so the shimmer never runs forever.
const PREVIEW_POLL_BASE_MS = 2000;
const PREVIEW_POLL_MAX_ATTEMPTS = 5;

export default {
  name: "CartItemList",

  components: { RenderShimmer },

  mixins: [helpers],

  inject: {
    // The store the page is for: the id StorefrontSessionGate resolved from
    // the URL slug (router/storefrontStoreId.js). Every cart RPC below names
    // it, so the server acts on THIS store's lines only.
    gateStoreId: { from: STOREFRONT_STORE_ID, default: null },
  },

  props: {
    cart: {
      type: Array,
      required: true,
    },
    campaign: {
      type: Object,
      required: true,
    },
    calculatingShipping: {
      type: Boolean,
      default: false,
    },
    salesTaxLoading: {
      type: Boolean,
      default: false,
    },
    fundraisingAmountPerItem: {
      type: Number,
      default: 0,
    },
    fundraisingType: {
      type: String,
      default: null,
    },
  },

  emits: ["quantity-changed", "cart-updated"],

  created() {
    // Not reactive: timer handles are bookkeeping, not render state.
    this.previewPollTimers = new Set();
  },

  data() {
    // Artifi PR 6b: per-cart-line preview state map keyed by the
    // cart line's stable ``line_id`` (per-store carts).
    // Lazy-fetched in beforeMount + watch; never mutated by the
    // cart-quantity handlers below (those still re-issue via
    // update_cart_quantity which refreshes the whole cart upstream).
    return {
      previewStates: {},
      // Empty Cart confirm dialog state.
      showEmptyCartDialog: false,
      emptyingCart: false,
      // Breakpoint state for the garment thumbnail. Defaults to desktop
      // so a context without matchMedia renders what shipped.
      isMobile: false,
      mobileMediaQuery: null,
      onBreakpointChange: null,
    };
  },

  computed: {
    // Drives BOTH axes of the thumbnail, so the image cannot be made
    // non-square by an edit that touches only one of them.
    thumbnailSize() {
      return this.isMobile ? THUMBNAIL_SIZE_MOBILE : THUMBNAIL_SIZE_DESKTOP;
    },

    // The Artifi cached-preview thumbnail, sized to match the garment
    // image it sits under. Read ``thumbnailSize`` rather than carrying
    // its own constants: the preview is inside the SAME
    // ``<v-col cols="2">``, so the phone constraint documented on
    // THUMBNAIL_SIZE_MOBILE applies to it identically, and a second
    // pair of constants would let the two images drift apart.
    //
    // Applied as inline STYLE, not the ``width``/``height`` content
    // attributes this element used before. Those attributes take a bare
    // integer -- ``width="90px"`` is invalid HTML and only resolves to
    // 90 through browser error-recovery -- whereas ``thumbnailSize`` is
    // a CSS length string, which is the correct type for style.
    previewBoxStyle() {
      return {
        width: this.thumbnailSize,
        height: this.thumbnailSize,
        objectFit: "cover",
        borderRadius: "4px",
      };
    },

    // The two placeholder branches reserve the IMAGE's box rather than
    // collapsing to the height of their own 10px caption.
    //
    // This is not hypothetical polish. ``previewState`` returns
    // ``status: "unavailable"`` for any row whose fetch has not resolved
    // yet, so the FIRST paint of every customizable line renders a
    // placeholder and then swaps to the image a round trip later. With
    // an unreserved box that swap moved the row by the difference
    // between a line of 10px text and the image -- about 46px at the old
    // 60px size, and it would have been about 76px at 90px. Reserving
    // the box makes the swap change only what is drawn inside it.
    //
    // ``inline-flex`` + centring is what gives a ``<span>`` a height at
    // all; as plain inline content it would ignore both dimensions.
    previewShimmerStyle() {
      return {
        width: this.thumbnailSize,
        height: this.thumbnailSize,
        borderRadius: "4px",
      };
    },

    previewPlaceholderStyle() {
      return {
        width: this.thumbnailSize,
        height: this.thumbnailSize,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontSize: "10px",
        color: "#888",
      };
    },
  },

  mounted() {
    this.refreshPreviews();
    this.subscribeToBreakpoint();
  },

  // A media-query listener that outlives the component writes into a dead
  // instance on the next rotation.
  beforeUnmount() {
    this.unsubscribeFromBreakpoint();
    for (const t of this.previewPollTimers) clearTimeout(t);
    this.previewPollTimers.clear();
  },

  watch: {
    cart() {
      // PR 6b: when the cart list shape changes (a row added /
      // removed) the previews need to be re-fetched. Indexed by
      // stable ``index`` so existing cached entries survive.
      this.refreshPreviews();
    },
  },

  methods: {
    /**
     * Track the mobile breakpoint for the garment thumbnail.
     *
     * `window.matchMedia` is absent in jsdom and in any SSR-ish context,
     * so its absence must degrade to "desktop" rather than throw -- the
     * desktop size is the safe default, being the one Jake specified.
     */
    subscribeToBreakpoint() {
      if (typeof window === "undefined" || !window.matchMedia) return;
      const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.mobileMediaQuery = mq;
      this.isMobile = mq.matches;
      this.onBreakpointChange = (event) => {
        this.isMobile = event.matches;
      };
      if (mq.addEventListener) {
        mq.addEventListener("change", this.onBreakpointChange);
      } else if (mq.addListener) {
        // Safari < 14 predates the EventTarget API on MediaQueryList.
        mq.addListener(this.onBreakpointChange);
      }
    },

    unsubscribeFromBreakpoint() {
      const mq = this.mobileMediaQuery;
      if (!mq || !this.onBreakpointChange) return;
      if (mq.removeEventListener) {
        mq.removeEventListener("change", this.onBreakpointChange);
      } else if (mq.removeListener) {
        mq.removeListener(this.onBreakpointChange);
      }
      this.mobileMediaQuery = null;
      this.onBreakpointChange = null;
    },

    logoName(product) {
      // The shopper-facing name of the logo THIS line carries, or "".
      //
      // Looked up by the line's own ``selected_artwork_id`` -- not by
      // taking the product's first logo, which would be right on the
      // single-logo products and silently wrong on the rest. Most
      // AdventHealth colour-rows offer more than one, and both strings
      // look equally plausible on screen, so a wrong pick here is the
      // kind of error nobody reports.
      //
      // ``logo_names`` arrives from getCampaignProductById as a JSON
      // object, so its keys are STRINGS while ``selected_artwork_id`` is
      // a number. The String() coercion is the whole reason this is a
      // method rather than an inline template expression -- an unco-
      // erced lookup misses every time and degrades to no caption,
      // which looks exactly like an undecorated line.
      //
      // "" (not null) so the template's v-if is a plain truthiness
      // check with one falsy shape rather than three.
      const artworkId = product && product.selected_artwork_id;
      if (artworkId === null || artworkId === undefined) return "";
      const names = (product && product.logo_names) || {};
      // A line whose COLOR offered exactly one logo gave the shopper no
      // choice, and the store decides whether that lone name is repeated
      // here (`show_single_logo`, Jake 2026-10-07, default off). Counted
      // per color off ``logo_counts_by_color`` -- the server builds it with
      // the PDP picker's own helper -- and judged by `hidesLoneLogo`, the
      // rule the PDP uses, so a garment whose picker was hidden is not
      // named here and vice versa. No count (an older payload) shows it.
      const counts = (product && product.logo_counts_by_color) || {};
      if (
        hidesLoneLogo(
          counts[product.primary_attribute_value],
          this.campaign && this.campaign.show_single_logo
        )
      ) {
        return "";
      }
      return names[String(artworkId)] || "";
    },

    // ---- Artifi PR 6b: personalization + preview helpers ----------

    isCustomizableLine(product) {
      // A cart row is "customizable" iff PR 6a captured at least one
      // personalization value into the underlying stored cart line.
      // Both list-shape (legacy storefront + PR 6a) and dict-shape
      // (PR 3 Artifi-handoff) carry personalization_values; this
      // helper accepts both.
      const pv = product && product.personalization_values;
      if (Array.isArray(pv)) {
        return pv.length > 0;
      }
      if (pv && typeof pv === "object") {
        return Object.keys(pv).length > 0;
      }
      return false;
    },

    personalizationValues(product) {
      // Returns the values as an ordered array of strings, regardless
      // of whether the underlying shape is a list (PR 6a) or a dict
      // (PR 3). Dict order is the insertion order which PR 3 already
      // sorts deterministically at write time.
      const pv = product && product.personalization_values;
      if (Array.isArray(pv)) {
        return pv;
      }
      if (pv && typeof pv === "object") {
        return Object.values(pv);
      }
      return [];
    },

    personalizationLabel(product, valueIndex) {
      // PR 6a's getCampaignProductById returns a flat
      // personalization_fields list (the union across all decorations
      // on the CP). PR 6a uses field [0] for the single-bucket capture
      // UI; PR 6b mirrors that. If fields are absent (e.g. legacy
      // hand-written cart data, or the field metadata didn't flow
      // through), fall back to "Personalization N".
      const fields = (product && product.personalization_fields) || [];
      // Prefer the matching field by index, then field[0] for the
      // single-bucket case, then the synthetic fallback.
      if (fields[valueIndex] && fields[valueIndex].label) {
        return fields[valueIndex].label;
      }
      if (fields[0] && fields[0].label) {
        return fields[0].label;
      }
      return `Personalization ${valueIndex + 1}`;
    },

    previewState(product) {
      // Returns the most recent cart-preview endpoint state for this
      // cart row. Defaults to ``{preview_url: null, status: "unknown"}``
      // before the first round trip resolves; the template renders the
      // 'unavailable' placeholder in that case so the row layout stays
      // stable from the first paint.
      const lineId = product && product.line_id;
      const state = lineId ? this.previewStates[lineId] : null;
      if (state) {
        return state;
      }
      return { preview_url: null, status: "unavailable" };
    },

    isPreviewPending(product) {
      const status = this.previewState(product).status;
      return status === "loading" || status === "rendering";
    },

    async refreshPreviews() {
      if (!Array.isArray(this.cart)) return;
      for (const product of this.cart) {
        if (!this.isCustomizableLine(product)) continue;
        const lineId = product.line_id;
        if (!lineId) continue;
        // Skip if we've already resolved this line -- the previews
        // are content-addressable; the cache state won't flip back to
        // cached after we've recorded an outcome. Both ``cached`` and
        // ``unavailable`` are sticky so cart-quantity edits don't
        // re-spam the endpoint.
        if (this.previewStates[lineId]) {
          continue;
        }
        // No store id, no cart request: the row keeps its placeholder.
        let storeArgs;
        try {
          storeArgs = cartStoreArgs(this.gateStoreId);
        } catch (e) {
          continue;
        }
        // Fire-and-forget; errors collapse to ``unavailable`` rather
        // than throwing into the cart UI.
        this.fetchPreview(lineId, storeArgs.store_id).catch(() => {});
      }
    },

    async fetchPreview(lineId, storeId, attempt = 0) {
      // CONCURRENCY NOTE: refreshPreviews fans this method out across
      // all customizable rows in parallel. Two concurrent fetches
      // resolving in the same microtask could otherwise read a stale
      // snapshot of previewStates and clobber each other's writes
      // (read-modify-write race). Per-key mutation via a stable
      // wrapper object avoids the spread; Vue 3 reactivity tracks
      // the wrapper itself, so nested writes still trigger renders.
      // Mounted -> refreshPreviews dispatches; we use this.$set-style
      // assignment via Object.assign on the wrapper.
      let nextValue;
      // Pending until the endpoint answers: the row shimmers rather than
      // flashing "Preview unavailable" for a render that is still coming.
      // Also marks the line as claimed, so refreshPreviews does not fetch
      // it twice while this one is in flight.
      if (attempt === 0) {
        this.previewStates[lineId] = { preview_url: null, status: "loading" };
      }
      try {
        // The line by its stable id, in the page's store's cart.
        const resp = await this.$axios.get(
          `/artifi/cart-preview?store_id=${storeId}` +
            `&line_id=${encodeURIComponent(lineId)}`
        );
        const data = (resp && resp.data) || {};
        nextValue = {
          preview_url: data.preview_url || null,
          status: data.status || "unavailable",
        };
      } catch (e) {
        // Auth, 404, or network error -- collapse to unavailable so
        // the row still renders cleanly. We deliberately do NOT
        // surface a banner; the personalization values are still
        // visible to the shopper so they can verify the row.
        nextValue = {
          preview_url: null,
          status: "unavailable",
        };
      }
      // Per-key write -- safe under concurrent fetches because each
      // call targets a distinct line_id. Vue 3's reactive proxy
      // intercepts the property set so the template re-renders.
      if (nextValue.status === "rendering" && !nextValue.preview_url) {
        if (attempt >= PREVIEW_POLL_MAX_ATTEMPTS) {
          // Out of retries: terminal, no shimmer.
          nextValue = { preview_url: null, status: "unavailable" };
        } else {
          const timer = setTimeout(() => {
            this.previewPollTimers.delete(timer);
            this.fetchPreview(lineId, storeId, attempt + 1).catch(() => {});
          }, PREVIEW_POLL_BASE_MS * 2 ** attempt);
          this.previewPollTimers.add(timer);
        }
      }
      this.previewStates[lineId] = nextValue;
    },

    applyFundraisingAmount(number) {
      if (this.fundraisingAmountPerItem) {
        if (this.fundraisingType === "percentage") {
          number = number / ((100 - this.fundraisingAmountPerItem) / 100);
        } else if (this.fundraisingType === "flat_rate") {
          number += this.fundraisingAmountPerItem;
        }
      }
      return this.formatCurrency(number);
    },

    // The cart line ALWAYS carries the resolved color/size directly as
    // ``primary_attribute_value`` / ``secondary_attribute_value`` -- for
    // BOTH new /shop (P6) products (whose campaignProduct
    // primary/secondary_attribute_name are null) and legacy named-attr
    // products. Read them straight off the line and pass them through to
    // the quantity/remove handlers. (The old code round-tripped the
    // values through a ``selectedValues`` map keyed by the attribute
    // NAMES; for /shop products both names are null, so the two writes
    // collided on the same null key and clobbered each other -- delete
    // and +/- silently targeted the wrong tuple and no-op'd.)
    //
    // ``selected_artwork_id`` is part of the line's identity too: two lines
    // of one product + color + size can differ only by logo. The backend
    // treats an ABSENT id as a wildcard matching the first same-size line,
    // so a handler that dropped it acted on the wrong logo's line.
    cartLineValues(item) {
      const cartProduct = this.cart
        .filter((obj) => obj.quantity > 0)
        .find((obj) => obj.index === item.index);
      return {
        primary_attribute_value: cartProduct.primary_attribute_value,
        secondary_attribute_value: cartProduct.secondary_attribute_value,
        selected_artwork_id: cartProduct.selected_artwork_id ?? null,
      };
    },

    // The RPC args naming one cart line. The logo is sent only when the
    // line has one, so an undecorated line keeps the exact payload it had
    // before logos existed (and the backend's wildcard still finds it).
    cartLineArgs(campaignProduct, values) {
      const args = {
        campaign_product_id: campaignProduct._jv.id,
        primary_attribute_value: values.primary_attribute_value,
        secondary_attribute_value: values.secondary_attribute_value,
        ...cartStoreArgs(this.gateStoreId),
      };
      if (values.selected_artwork_id != null) {
        args.selected_artwork_id = values.selected_artwork_id;
      }
      return args;
    },

    increaseQuantity(item) {
      const values = this.cartLineValues(item);
      this.addProductHandler(item.campaignProduct, values, 1);
    },

    decreaseQuantity(item) {
      const values = this.cartLineValues(item);
      this.addProductHandler(item.campaignProduct, values, -1);
    },

    /**
     * Commit a TYPED line quantity.
     *
     * Converts the absolute value the shopper typed into the delta
     * `addProductHandler` expects, rather than adding a second write path.
     * That handler owns the min/max validation and the server-side merge,
     * so routing through it keeps one set of rules for both entry modes --
     * a typed value cannot become a way around a campaign's max_quantity.
     *
     * Floors at 1: see the template comment on the input.
     */
    onQuantityCommit(item, event) {
      const raw = event && event.target ? event.target.value : "";
      const parsed = Math.floor(Number(raw));
      const current = Number(item.quantity);

      // An emptied or unparseable field is an ABANDONED edit, not a
      // request to delete the line. Restore what is still in the cart.
      if (raw === "" || raw == null || !Number.isFinite(parsed)) {
        if (event && event.target) event.target.value = current;
        return;
      }

      const next = Math.min(9999, Math.max(1, parsed));
      if (event && event.target && next !== parsed) {
        // Reflect the clamp, so the field never shows a value the cart
        // does not hold.
        event.target.value = next;
      }
      if (next === current) return;

      const values = this.cartLineValues(item);
      this.addProductHandler(item.campaignProduct, values, next - current);
    },

    async deleteItem(item) {
      const values = this.cartLineValues(item);
      this.removeProductHandler(item.campaignProduct, values);
    },

    quantityDecreaseDisabled(item) {
      return item.quantity <= 1;
    },

    quantityIncreaseDisabled(item) {
      return false;
    },

    // ``values`` carries the cart line's resolved
    // ``primary_attribute_value`` / ``secondary_attribute_value``
    // (color + size) and ``selected_artwork_id`` DIRECTLY -- see
    // cartLineValues(). These are the keys the backend matches a cart
    // line on (campaign_product_id + primary + secondary + logo); passing
    // them through untouched works for both null-attr-name /shop products
    // and legacy named-attr products.
    async addProductHandler(campaignProduct, values, quantity) {
      const primary_attribute_value = values.primary_attribute_value;
      const secondary_attribute_value = values.secondary_attribute_value;
      const logo = artworkKey(values.selected_artwork_id);

      // determine if we are updating the quantity of a product in the cart
      // already -- THIS line, logo included, not the first same-size one
      // ``state.cart`` is THIS store's cart (StorefrontCart's loadCart).
      const matchingItems = this.$store.state.cart.filter((obj) => {
        return (
          obj.campaign_product_id == campaignProduct._jv.id &&
          obj.primary_attribute_value === primary_attribute_value &&
          obj.secondary_attribute_value === secondary_attribute_value &&
          artworkKey(obj.selected_artwork_id) === logo
        );
      });
      if (matchingItems.length > 0) {
        quantity += matchingItems[0].quantity;
      }

      if (
        campaignProduct.max_quantity &&
        campaignProduct.max_quantity < quantity
      ) {
        this.$store.dispatch(
          "raiseError",
          `Max quantity for this product is ${campaignProduct.max_quantity}`
        );
        return;
      } else if (
        campaignProduct.min_quantity &&
        campaignProduct.min_quantity > quantity
      ) {
        this.$store.dispatch(
          "raiseError",
          `Min quantity for this product is ${campaignProduct.min_quantity}`
        );
        return;
      }

      const response = await this.$axios.post(
        `/accounts/${this.$store.state.account._jv.id}/update_cart_quantity`,
        {
          meta: {
            method: "update_cart_quantity",
            args: {
              ...this.cartLineArgs(campaignProduct, values),
              quantity: quantity,
              prevent_cart_update_on_error: true,
            },
          },
        }
      );
      this.$store.commit("setCart", response.data.meta.result.cart);
      this.$emit("cart-updated");
    },

    async removeProductHandler(campaignProduct, values) {
      const response = await this.$axios.post(
        `/accounts/${this.$store.state.account._jv.id}/update_cart_quantity`,
        {
          meta: {
            method: "update_cart_quantity",
            args: {
              ...this.cartLineArgs(campaignProduct, values),
              quantity: 0,
              prevent_cart_update_on_error: true,
            },
          },
        }
      );
      this.$store.commit("setCart", response.data.meta.result.cart);
      this.$emit("cart-updated");
    },

    // Empty THIS STORE's cart in one atomic call. Uses the backend
    // ``delete_cart`` RPC, naming the page's store (it empties that store's
    // cart and no other), rather than looping
    // update_cart_quantity(qty 0) per line -- one round-trip, no
    // partial-empty window. Commit the store's cart the server returns
    // (empty), then emit ``cart-updated`` so
    // StorefrontCart re-loads and the summary/shipping recompute. The dialog closes regardless so a
    // network error doesn't trap the shopper behind a modal (the error
    // surfaces via raiseError and the cart re-loads to truth on the
    // next handleCartUpdated).
    async confirmEmptyCart() {
      this.emptyingCart = true;
      try {
        const response = await this.$axios.post(
          `/accounts/${this.$store.state.account._jv.id}/delete_cart`,
          {
            meta: {
              method: "delete_cart",
              args: cartStoreArgs(this.gateStoreId),
            },
          }
        );
        const remaining = response?.data?.meta?.result;
        this.$store.commit("setCart", Array.isArray(remaining) ? remaining : []);
        this.$emit("cart-updated");
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      } finally {
        this.emptyingCart = false;
        this.showEmptyCartDialog = false;
      }
    },
  },
};
</script>
